<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ReplaceMeter',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const form = ref(null);

  const $refs = {
    get form() {
      return form.value;
    },
  };

  const formId = ref(null);
  const labelAlign = ref('right');
  const item = ref(null);
  const responseData = ref(null);
  const workflowStepsInfo = ref({
    steps: [
      'rögzíti a leszerelt mérőóra állását',
      'inaktiválja a leszerelt órát',
      'inaktiválja a kapcsolódó bérleménynél beállított mért szolgáltatást',
      'létrehoz egy új mérőórát a megadott sorozatszámmal',
      'rögzíti az induló mérőállást',
      'a kapcsolódó bérleménynél a leszerelt mérőóra helyett beállít egy mért szolgáltatást az új mérőórával',
    ],
  });
  const units = ref(null);
  const selectedOldMeter = ref(null);
  const old_reading_image = ref(null);
  const new_reading_image = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_4963030162390099_dataSource = computed(() => ({
    name: 'suggest_meter',
    parameters: {
      exclude_types: ['pulse'],
    },
  }));
  const b_4246589045129675_dataSource = computed(() => ({
    name: 'suggest_meter',
    parameters: {
      exclude_types: ['pulse'],
    },
  }));

  const $data = computed(() => ({
    formId: formId.value,
    labelAlign: labelAlign.value,
    item: item.value,
    responseData: responseData.value,
    workflowStepsInfo: workflowStepsInfo.value,
    units: units.value,
    selectedOldMeter: selectedOldMeter.value,
    old_reading_image: old_reading_image.value,
    new_reading_image: new_reading_image.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_4963030162390099_dataSource: b_4963030162390099_dataSource.value,
    b_4246589045129675_dataSource: b_4246589045129675_dataSource.value,
  }));

  const $$load_item = async () => {
    return $fn.value.schemaDefaults('ReplaceMeter');
  };
  const $$load_units = async () => {
    return $fn.value.fetch('get_meter_type_units');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'item' },
      { name: 'responseData' },
      { name: 'workflowStepsInfo' },
      { name: 'units' },
      { name: 'selectedOldMeter' },
      { name: 'old_reading_image' },
      { name: 'new_reading_image' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_4963030162390099_dataSource', nodeId: '4963030162390099' },
      { name: 'b_4246589045129675_dataSource', nodeId: '4246589045129675' },
    ],
    loaders: { item: $$load_item, units: $$load_units },
    ux: {},
    dataRefs: {
      formId,
      labelAlign,
      item,
      responseData,
      workflowStepsInfo,
      units,
      selectedOldMeter,
      old_reading_image,
      new_reading_image,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_4963030162390099_dataSource,
      b_4246589045129675_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadOnly = computed(() => {
    if (loaded.value) {
      return !!(
        responseData.value ||
        (actions.submit && actions.submit.isRunning)
      );
    }
    return null;
  });
  const oldMeterReadingUnitOptions = computed(() => {
    if (loaded.value) {
      return selectedOldMeter.value
        ? units.value[selectedOldMeter.value.meter_type]
        : [];
    }
    return null;
  });
  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('ReplaceMeterModal.Title', 'Replace Meter'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_w6qrzmzes_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_setData_yfg9z8e04_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'responseData',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_request_x53k0f7f9_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_meter_operation',
      data: item.value,
      parameters: {
        op: 'replacement',
      },
    };
  };
  const act_4963030162390099_setData_36nqjot5l_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.old_meter.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4963030162390099_setData_ig3ws4y55_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedOldMeter',
      value: $event.data.option,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3855552110905499_setData_f3m48qvm9_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.old_meter.image',
      value: $fn.value.imageData($event.value),
      resolve: true,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4246589045129675_setData_ld9rbz52d_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.new_meter.serial',
      value: $event.data.option.serial_number,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4246589045129675_setData_ld9rbz52d_getWhen = async ($event) => {
    return $event.data.option;
  };
  const act_4246589045129675_setData_xnvugin4e_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.new_meter.id',
      value: $event.data.option.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4246589045129675_setData_xnvugin4e_getWhen = async ($event) => {
    return $event.data.option;
  };
  const act_4246589045129675_setData_tjnhe5uvc_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.new_meter.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4246589045129675_setData_tjnhe5uvc_getWhen = async ($event) => {
    return $event.data.option;
  };

  const act_7315092382398562_closeModal_w6qrzmzes =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_w6qrzmzes_getActionArgs,
    });
  const inst_evh_7315092382398562_close_6aqrz4y4o =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_w6qrzmzes,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_6aqrz4y4o],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_nnjs8nque =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_rp1m7vphd =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_nnjs8nque,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_rp1m7vphd],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_hdkr1gxr6 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_rnslpnpxx =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_hdkr1gxr6,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_rnslpnpxx],
    event: 'close',
  });
  const act_1040167445267876_setData_yfg9z8e04 = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_yfg9z8e04_getActionArgs,
  });
  const inst_evh_1040167445267876_success_6wdmjmvrv =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_yfg9z8e04,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_request_x53k0f7f9 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_x53k0f7f9_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_6wdmjmvrv],
  });
  const inst_evh_1040167445267876_submit_osdktpbd7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_x53k0f7f9,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_osdktpbd7],
    event: 'submit',
  });
  const act_4963030162390099_setData_36nqjot5l = new actionTypes.SetDataAction({
    actionArgs: act_4963030162390099_setData_36nqjot5l_getActionArgs,
  });
  const inst_evh_4963030162390099_optionSelected_cg67mntc6 =
    new actionTypes.EventHandlerImpl({
      action: act_4963030162390099_setData_36nqjot5l,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_4963030162390099_setData_ig3ws4y55 = new actionTypes.SetDataAction({
    actionArgs: act_4963030162390099_setData_ig3ws4y55_getActionArgs,
  });
  const inst_evh_4963030162390099_optionSelected_orcdspmdf =
    new actionTypes.EventHandlerImpl({
      action: act_4963030162390099_setData_ig3ws4y55,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_4963030162390099_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_4963030162390099_optionSelected_cg67mntc6,
        inst_evh_4963030162390099_optionSelected_orcdspmdf,
      ],
      event: 'optionSelected',
    });
  const act_3855552110905499_setData_f3m48qvm9 = new actionTypes.SetDataAction({
    actionArgs: act_3855552110905499_setData_f3m48qvm9_getActionArgs,
  });
  const inst_evh_3855552110905499_changed_scc18wq4z =
    new actionTypes.EventHandlerImpl({
      action: act_3855552110905499_setData_f3m48qvm9,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_3855552110905499_changed = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3855552110905499_changed_scc18wq4z],
    event: 'changed',
  });
  const act_4246589045129675_setData_ld9rbz52d = new actionTypes.SetDataAction({
    actionArgs: act_4246589045129675_setData_ld9rbz52d_getActionArgs,
    when: act_4246589045129675_setData_ld9rbz52d_getWhen,
  });
  const inst_evh_4246589045129675_optionSelected_nzcpzt5ve =
    new actionTypes.EventHandlerImpl({
      action: act_4246589045129675_setData_ld9rbz52d,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_4246589045129675_setData_xnvugin4e = new actionTypes.SetDataAction({
    actionArgs: act_4246589045129675_setData_xnvugin4e_getActionArgs,
    when: act_4246589045129675_setData_xnvugin4e_getWhen,
  });
  const inst_evh_4246589045129675_optionSelected_epwm1n0zp =
    new actionTypes.EventHandlerImpl({
      action: act_4246589045129675_setData_xnvugin4e,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_4246589045129675_setData_tjnhe5uvc = new actionTypes.SetDataAction({
    actionArgs: act_4246589045129675_setData_tjnhe5uvc_getActionArgs,
    when: act_4246589045129675_setData_tjnhe5uvc_getWhen,
  });
  const inst_evh_4246589045129675_optionSelected_4puz0peb9 =
    new actionTypes.EventHandlerImpl({
      action: act_4246589045129675_setData_tjnhe5uvc,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_4246589045129675_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_4246589045129675_optionSelected_nzcpzt5ve,
        inst_evh_4246589045129675_optionSelected_epwm1n0zp,
        inst_evh_4246589045129675_optionSelected_4puz0peb9,
      ],
      event: 'optionSelected',
    });
  const act_353388685162594_closeComponent_nea8n12cu =
    new actionTypes.CloseComponentAction({});
  const inst_evh_353388685162594_clickPreventStop_ag0zlfdcd =
    new actionTypes.EventHandlerImpl({
      action: act_353388685162594_closeComponent_nea8n12cu,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_353388685162594_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_353388685162594_clickPreventStop_ag0zlfdcd],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_closeComponent_t3oj9a8yp =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2140198529698299_clickPreventStop_9874pvy2b =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_t3oj9a8yp,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_9874pvy2b],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_6aqrz4y4o,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_rp1m7vphd,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_rnslpnpxx,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_6wdmjmvrv,
    inst_evh_1040167445267876_submit_osdktpbd7,
    submit: inst_evh_1040167445267876_submit_osdktpbd7,
    inst_evh_1040167445267876_submit,
    inst_evh_4963030162390099_optionSelected_cg67mntc6,
    inst_evh_4963030162390099_optionSelected_orcdspmdf,
    inst_evh_4963030162390099_optionSelected,
    inst_evh_3855552110905499_changed_scc18wq4z,
    inst_evh_3855552110905499_changed,
    inst_evh_4246589045129675_optionSelected_nzcpzt5ve,
    inst_evh_4246589045129675_optionSelected_epwm1n0zp,
    inst_evh_4246589045129675_optionSelected_4puz0peb9,
    inst_evh_4246589045129675_optionSelected,
    inst_evh_353388685162594_clickPreventStop_ag0zlfdcd,
    inst_evh_353388685162594_clickPreventStop,
    inst_evh_2140198529698299_clickPreventStop_9874pvy2b,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_6aqrz4y4o = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_6aqrz4y4o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_rp1m7vphd = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_rp1m7vphd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_rnslpnpxx = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_rnslpnpxx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_6wdmjmvrv = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_6wdmjmvrv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_osdktpbd7 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_osdktpbd7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4963030162390099_optionSelected_cg67mntc6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4963030162390099_optionSelected_cg67mntc6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4963030162390099_optionSelected_orcdspmdf = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4963030162390099_optionSelected_orcdspmdf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4963030162390099_optionSelected = async (event, scope) => {
    return await actions.inst_evh_4963030162390099_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3855552110905499_changed_scc18wq4z = async (event, scope) => {
    return await actions.inst_evh_3855552110905499_changed_scc18wq4z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3855552110905499_changed = async (event, scope) => {
    return await actions.inst_evh_3855552110905499_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4246589045129675_optionSelected_nzcpzt5ve = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4246589045129675_optionSelected_nzcpzt5ve.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4246589045129675_optionSelected_epwm1n0zp = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4246589045129675_optionSelected_epwm1n0zp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4246589045129675_optionSelected_4puz0peb9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4246589045129675_optionSelected_4puz0peb9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4246589045129675_optionSelected = async (event, scope) => {
    return await actions.inst_evh_4246589045129675_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_353388685162594_clickPreventStop_ag0zlfdcd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_353388685162594_clickPreventStop_ag0zlfdcd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_353388685162594_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_353388685162594_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_9874pvy2b = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_9874pvy2b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ReplaceMeter"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="replaceMeter"
        :id="formId"
        ref="form"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpAlert show>
          <div>
            Az óracsere művelet a következő lépéseket végzi el automatikusan:
          </div>
          <ul>
            <li v-for="step in workflowStepsInfo.steps">
              {{ step }}
            </li>
          </ul>
        </ItpAlert>
        <hr />
        <ItpFormGroup
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="3"
          name="replacement_time"
          :label="pgettext('ReplaceMeterModal', 'Replacement time')"
          required
        >
          <ItpFormDatetime
            type="datetime"
            name="replacement_time"
            v-model="item.replacement_time"
            required
            :readonly="isReadOnly"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <hr />
        <div class="form-group">
          <ItpText
            strong
            muted
            :text="pgettext('ReplaceMeterModal', 'Old device')"
          >
          </ItpText>
        </div>
        <ItpFormGroup
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
          name="format"
          :label="pgettext('ReplaceMeterModal', 'Meter')"
          required
        >
          <ItpSelect2
            name="old_meter"
            required
            :readonly="isReadOnly"
            v-model="item.old_meter.id"
            :value="item.old_meter.id"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.old_meter.id =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :dataSource="b_4963030162390099_dataSource"
            valueField="id"
            textField="serial_number"
            use-option-template-for-selected-option
            optionTemplate="<UcSuggestMeterOption :option=option></UcSuggestMeterOption>"
            @optionSelected="evh_4963030162390099_optionSelected($event, {})"
          >
          </ItpSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
          name="old_reading_value"
          :label="pgettext('ReplaceMeterModal', 'Reading value')"
          required
        >
          <ItpBox>
            <ItpFormInput
              type="number"
              class="col-3"
              name="old_reading_value"
              v-model="item.old_meter.reading_value"
              :value="item.old_meter.reading_value"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.old_meter.reading_value =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              required
              :readonly="isReadOnly"
            >
            </ItpFormInput>
            <ItpFormSelect2
              class="col-4"
              name="old_reading_unit"
              v-model="item.old_meter.unit"
              :value="item.old_meter.unit"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.old_meter.unit =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :options="oldMeterReadingUnitOptions"
              required
              :readonly="isReadOnly"
            >
            </ItpFormSelect2>
          </ItpBox>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          name="old_reading_image"
          :label="pgettext('ReplaceMeter', 'Photo')"
          :label-cols-md="3"
          :content-cols-md="9"
        >
          <ItpFormFile
            v-model="old_reading_image"
            :value="old_reading_image"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (old_reading_image =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            name="old_reading_image"
            :placeholder="
              pgettext('ReplaceMeter', 'The photo of the gauge if available.')
            "
            lang="hu"
            @changed="evh_3855552110905499_changed($event, {})"
          >
          </ItpFormFile>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          name="old_notes"
          :label="pgettext('ReplaceMeterModal', 'Notes')"
          :label-cols-md="3"
          :content-cols-md="9"
        >
          <ItpFormTextarea
            name="old_notes"
            v-model="item.old_meter.notes"
            :value="item.old_meter.notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.old_meter.notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
        <hr />
        <div class="form-group">
          <ItpText
            strong
            muted
            :text="pgettext('ReplaceMeterModal', 'New device')"
          >
          </ItpText>
        </div>
        <ItpFormGroup
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
          name="new_serial"
          :label="pgettext('ReplaceMeterModal', 'Device')"
        >
          <ItpSelect2
            name="new_meter"
            required
            :readonly="isReadOnly"
            v-model="item.new_meter.serial"
            :value="item.new_meter.serial"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.new_meter.serial =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :dataSource="b_4246589045129675_dataSource"
            valueField="serial_number"
            textField="serial_number"
            taggable
            clearable
            optionTemplate="<UcSuggestMeterOption :option=option></UcSuggestMeterOption>"
            @optionSelected="evh_4246589045129675_optionSelected($event, {})"
          >
          </ItpSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
          name="new_serial"
          :label="pgettext('ReplaceMeterModal', 'Serial number')"
          required
        >
          <ItpFormInput
            name="new_serial"
            v-model="item.new_meter.serial"
            :value="item.new_meter.serial"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.new_meter.serial =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
          name="new_deveui"
          :label="pgettext('ReplaceMeterModal', 'DevEUI')"
        >
          <ItpFormInput
            name="new_deveui"
            v-model="item.new_meter.deveui"
            :value="item.new_meter.deveui"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.new_meter.deveui =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
          name="new_tag_id"
          :label="pgettext('ReplaceMeterModal', 'Location Code')"
        >
          <ItpFormInput
            name="new_tag_id"
            v-model="item.new_meter.tag_id"
            :value="item.new_meter.tag_id"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.new_meter.tag_id =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
          name="new_reading_value"
          :label="pgettext('ReplaceMeterModal', 'Reading value')"
          required
        >
          <ItpBox>
            <ItpFormInput
              type="number"
              class="col-3"
              name="new_reading_value"
              v-model="item.new_meter.reading_value"
              :value="item.new_meter.reading_value"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.new_meter.reading_value =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              required
              :readonly="isReadOnly"
            >
            </ItpFormInput>
            <ItpFormSelect2
              class="col-4"
              name="new_reading_unit"
              v-model="item.new_meter.unit"
              :value="item.new_meter.unit"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.new_meter.unit =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :options="oldMeterReadingUnitOptions"
              required
              :readonly="isReadOnly"
            >
            </ItpFormSelect2>
          </ItpBox>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          name="new_reading_image"
          :label="pgettext('ReplaceMeter', 'Photo')"
          :label-cols-md="3"
          :content-cols-md="9"
        >
          <ItpFormFile
            v-model="new_reading_image"
            :value="new_reading_image"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (new_reading_image =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            name="new_reading_image"
            :placeholder="
              pgettext('ReplaceMeter', 'The photo of the gauge if available.')
            "
            lang="hu"
          >
          </ItpFormFile>
        </ItpFormGroup>
        <ItpFormGroup
          :labelAlign="labelAlign"
          name="new_notes"
          :label="pgettext('ReplaceMeterModal', 'Notes')"
          :label-cols-md="3"
          :content-cols-md="9"
        >
          <ItpFormTextarea
            name="new_notes"
            v-model="item.new_meter.notes"
            :value="item.new_meter.notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.new_meter.notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
        <hr />
        <div class="row mb-3">
          <div class="col-md-9 offset-md-3">
            <ItpButton
              :form="formId"
              :text="pgettext('ReplaceMeterModal', 'Replace Meter')"
              type="submit"
              variant="primary"
              :icon="$config.meterReplacement.icon"
              :spinning="actions.submit.isRunning"
              :disabled="
                !!(
                  !forms.replaceMeter ||
                  !forms.replaceMeter.submittable ||
                  actions.submit.isRunning ||
                  responseData
                )
              "
            >
            </ItpButton>
          </div>
        </div>
        <ItpAlert type="success" show v-if="responseData">
          <h4 class="alert-heading">Sikeres óracsere</h4>
          <ul>
            <li v-for="step in responseData.value.result">
              {{ step.message }}
            </li>
          </ul>
          <div class="text-center">
            <ItpButton
              :text="gettext('Close')"
              variant="primary"
              :disabled="actions.submit.isRunning"
              @click.prevent.stop="
                evh_353388685162594_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </div>
        </ItpAlert>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
